/**
 * WeatherPlaceholderView Component
 * P2.7-2: Live weather via Open-Meteo free API (no key required)
 * Location configurable via VITE_WEATHER_LAT / VITE_WEATHER_LON env vars.
 * Defaults to Lisbon, Portugal (38.7223, -9.1393).
 *
 * Data is fetched via the shared `useWeather` hook so the sidebar widget and
 * this full view always show identical figures.
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWeather } from '@/hooks';

interface WeatherPlaceholderViewProps {
  isActive: boolean;
  onClose: () => void;
}

export function WeatherPlaceholderView({ isActive, onClose }: WeatherPlaceholderViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const { weather, loading, refresh } = useWeather();
  const error = !loading && !weather ? 'Unable to load weather. Check your connection.' : null;

  // Use ref to track isActive so keyboard handler always reads current value
  const isActiveRef = useRef(isActive);
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  // Keyboard navigation
  useEffect(() => {
    const forecastLen = weather?.forecast.length ?? 5;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isActiveRef.current) return;
      if (e.defaultPrevented) return;

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          e.stopImmediatePropagation();
          onClose();
          break;
        case 'ArrowUp':
          e.preventDefault();
          e.stopImmediatePropagation();
          setSelectedDayIndex(prev => (prev > 0 ? prev - 1 : forecastLen - 1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          e.stopImmediatePropagation();
          setSelectedDayIndex(prev => (prev < forecastLen - 1 ? prev + 1 : 0));
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose, weather?.forecast.length]);

  // Focus container on activation
  useEffect(() => {
    if (isActive && containerRef.current) {
      containerRef.current.focus();
    }
  }, [isActive]);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------
  const renderLoading = () => (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-teal-500/40 border-t-teal-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/40 text-sm">Fetching live weather…</p>
      </div>
    </div>
  );

  const renderError = () => (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="text-center">
        <span className="text-5xl mb-4 block">⛅</span>
        <p className="text-white/60 text-sm mb-3">{error}</p>
        <button
          onClick={refresh}
          className="px-4 py-2 bg-teal-600/30 hover:bg-teal-600/50 border border-teal-500/30 rounded-lg text-teal-300 text-sm transition-colors"
        >
          Retry
        </button>
      </div>
    </div>
  );

  return (
    <motion.div
      ref={containerRef}
      tabIndex={-1}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="weather-view h-full w-full bg-zinc-950 flex flex-col outline-none overflow-hidden rounded-xl"
    >
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-2 bg-black/30 flex items-center justify-between">
        <span className="text-white/50 text-xs">📍 {weather?.location ?? 'Loading…'}</span>
        <button
          onClick={refresh}
          className="text-white/30 hover:text-white/60 text-xs transition-colors"
          title="Refresh weather"
        >
          ↻ Refresh
        </button>
      </div>

      {loading && renderLoading()}
      {!loading && error && renderError()}

      {!loading && !error && weather && (
        <div className="flex-1 overflow-y-auto p-6">
          {/* Current Weather */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="weather-current bg-gradient-to-br from-blue-900/40 to-zinc-900 rounded-2xl p-6 mb-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm">Current Weather</p>
                <div className="flex items-end gap-2 mt-2">
                  <span className="text-6xl font-light text-white">{weather.current.temp}°</span>
                  <span className="text-white/60 text-lg mb-2">C</span>
                </div>
                <p className="text-white/80 mt-1">{weather.current.condition}</p>
              </div>
              <span className="text-8xl">{weather.current.icon}</span>
            </div>
            <div className="flex gap-6 mt-6 pt-4 border-t border-white/10">
              <div>
                <p className="text-white/40 text-xs">Humidity</p>
                <p className="text-white font-medium">{weather.current.humidity}%</p>
              </div>
              <div>
                <p className="text-white/40 text-xs">Wind</p>
                <p className="text-white font-medium">{weather.current.wind} km/h</p>
              </div>
            </div>
          </motion.div>

          {/* 5-Day Forecast */}
          <div className="weather-forecast">
          <h2 className="text-white/60 text-sm uppercase tracking-wider mb-4">5-Day Forecast</h2>
          <div className="space-y-2">
            {weather.forecast.map((day, idx) => (
              <div key={day.day}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                className={`rounded-lg p-4 flex items-center justify-between cursor-pointer transition-all ${
                  idx === selectedDayIndex
                    ? 'bg-teal-600/30 ring-2 ring-teal-500 rounded-b-none'
                    : 'bg-white/5 hover:bg-white/10'
                }`}
                onClick={() => setSelectedDayIndex(idx)}
              >
                <span className="text-white w-24">{day.day}</span>
                <span className="text-3xl">{day.icon}</span>
                <div className="flex items-center gap-4">
                  <span className="text-white font-medium">{day.high}°</span>
                  <span className="text-white/40">{day.low}°</span>
                </div>
              </motion.div>

              {/* Expanded detail for selected day */}
              <AnimatePresence>
                {idx === selectedDayIndex && (
                  <motion.div
                    key={`detail-${idx}`}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="bg-teal-950/40 border border-teal-500/20 border-t-0 rounded-b-lg px-4 py-3 grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-white/40 text-xs mb-1">🌧 Rain</p>
                        <p className="text-white/80 text-sm font-medium">{day.precipitation} mm</p>
                      </div>
                      <div>
                        <p className="text-white/40 text-xs mb-1">💨 Wind</p>
                        <p className="text-white/80 text-sm font-medium">{day.wind} km/h</p>
                      </div>
                      <div>
                        <p className="text-white/40 text-xs mb-1">☀️ UV</p>
                        <p className="text-white/80 text-sm font-medium">{day.uvIndex}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              </div>
            ))}
          </div>
          </div>
        </div>
      )}

    </motion.div>
  );
}

export default WeatherPlaceholderView;
